/**
 * `useAiQuotaUsage` — per-workspace AI usage state for the Billing tab and
 * the soft 80 % warning toast.
 *
 * Powered by `GET /api/quota/me`. Stale time matches Billing data
 * (5 minutes) — this is a low-velocity read; counters tick up server-side
 * at AI-call time, but the user-visible card doesn't need real-time
 * accuracy. Refetched on workspace switch via the queryKey.
 *
 * On a TEAM workspace every feature reports `cap === null` (unlimited)
 * and `percentUsed === 0`. The component layer is expected to render a
 * "Unlimited" pill rather than a 0 % progress bar in that case.
 */

import { useQuery } from '@tanstack/react-query';

import { apiService, type AiQuotaState } from '../../services/api.service';
import { getAiQuotaUsage } from '../../services/api/billing.api';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { queryKeys, STALE_TIME } from '../../lib/queryClient';

export interface AiQuotaSummary {
  readonly chat: AiQuotaState;
  readonly liveMinutes: AiQuotaState;
  readonly textExtractions: AiQuotaState;
  readonly transcriptionMinutes: AiQuotaState;
  readonly codeExecutionMinutes: AiQuotaState;
}

export function useAiQuotaUsage() {
  const workspaceId = useWorkspaceId();
  const { user, bootLoading, actionLoading } = useAuth();
  const authLoading = bootLoading || actionLoading;

  return useQuery<AiQuotaSummary>({
    queryKey: queryKeys.aiQuota.me(workspaceId || ''),
    queryFn: async () => {
      const response = await getAiQuotaUsage(apiService);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch AI quota');
      }
      return response.data;
    },
    // Wait for both workspaceId (from localStorage) AND the auth token to
    // be in place — same posture as `useWorkspacePlan`. Avoids the 401
    // bounce on hard refresh while Firebase restores the token.
    enabled: !!workspaceId && !!user && !authLoading,
    staleTime: STALE_TIME.WORKSPACE,
  });
}
